import React, { useState } from 'react';
import { Box, Collapse, RangeSlider, RangeSliderFilledTrack, RangeSliderThumb, RangeSliderTrack, Text } from '@chakra-ui/react';
import { Check, ExpandMore, Add } from '@mui/icons-material';

import { swatchColor } from './product/productUtils';

// Collection filters (design-system.md §5.3): collapsible groups of checkbox rows with "+ Show more",
// colour swatches, sizes taken from the products on screen, and a price range.

// Named colours the admin product editor offers as quick picks
export const COLOR_SWATCHES = {
    black: '#1a1a1a', white: '#ffffff', grey: '#9ca3af', navy: '#1e2a4a', blue: '#2b59c3',
    green: '#2f9e44', beige: '#d6c3a1', brown: '#7a4e2d', red: '#c92a2a', pink: '#f4a6c0'
};

export const emptyFilters = (priceBounds) => ({ categories: [], sizes: [], colors: [], price: priceBounds });

export const countActiveFilters = (filters, priceBounds) =>
    filters.categories.length + filters.sizes.length + filters.colors.length +
    (filters.price[0] > priceBounds[0] || filters.price[1] < priceBounds[1] ? 1 : 0);

export const applyFilters = (products, filters) => products.filter(p =>
    (!filters.categories.length || filters.categories.includes(p.category)) &&
    (!filters.colors.length || filters.colors.includes(p.color)) &&
    (!filters.sizes.length || filters.sizes.some(size => (p.sizes || []).includes(size))) &&
    p.price >= filters.price[0] && p.price <= filters.price[1]
);

const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'];
export const sortSizes = (sizes) => [...sizes].sort((a, b) => {
    const ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b);
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return Number(a) - Number(b);
});

const toggle = (list, value) => list.includes(value) ? list.filter(v => v !== value) : [...list, value];

export const CheckRow = ({ checked, onChange, children, swatch }) => (
    <Box as='label' display='flex' alignItems='center' gap='10px' py='6px' cursor='pointer' fontSize={{ base: '14px', md: '14.4px' }} lineHeight='21.6px' color='raano.black' >
        <Box as='input' type='checkbox' checked={checked} onChange={onChange} position='absolute' opacity={0} width='1px' height='1px' />
        <Box width='16px' height='16px' flexShrink={0} border='1px solid' borderColor={checked ? 'raano.black' : 'blackAlpha.500'} bg={checked ? 'raano.black' : 'raano.white'} borderRadius='3px' display='flex' alignItems='center' justifyContent='center' color='raano.white' transition='all .125s'
            sx={{ 'input:focus-visible + &': { outline: '2px solid', outlineColor: 'raano.black', outlineOffset: '2px' } }} >
            {checked && <Check sx={{ fontSize: 13 }} />}
        </Box>
        {swatch && <Box width='20px' height='20px' borderRadius='4px' border='1px solid' borderColor='blackAlpha.200' bg={swatch} flexShrink={0} />}
        <Box as='span' textTransform='capitalize' >{children}</Box>
    </Box>
);

const Group = ({ title, children, defaultOpen = true, count }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Box borderBottom='1px solid' borderColor='raano.border' py='12px' >
            <Box as='button' type='button' width='100%' display='flex' alignItems='center' justifyContent='space-between' onClick={() => setOpen(o => !o)} aria-expanded={open} >
                <Text fontSize={{ base: '14px', md: '16.2px' }} lineHeight='24.3px' color='raano.ink' >{title}{count ? ` (${count})` : ''}</Text>
                <ExpandMore sx={{ fontSize: 20, transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }} />
            </Box>
            <Collapse in={open} animateOpacity ><Box pt='6px' >{children}</Box></Collapse>
        </Box>
    );
};

const ShowMoreList = ({ items, limit = 5, render }) => {
    const [all, setAll] = useState(false);
    const shown = all ? items : items.slice(0, limit);
    return (
        <>
            {shown.map(render)}
            {items.length > limit &&
                <Box as='button' type='button' display='flex' alignItems='center' gap='4px' mt='4px' fontSize='12px' color='blackAlpha.700' onClick={() => setAll(a => !a)} >
                    <Add sx={{ fontSize: 14 }} />{all ? 'Show less' : 'Show more'}
                </Box>}
        </>
    );
};

export const PriceRange = ({ value, bounds, onChange }) => (
    <Box>
        <Box display='flex' justifyContent='space-between' mb='6px' fontSize='14px' >
            <Text>₹{value[0].toLocaleString('en-IN')}</Text>
            <Text>₹{value[1].toLocaleString('en-IN')}</Text>
        </Box>
        <Box px='8px' >
            <RangeSlider min={bounds[0]} max={bounds[1]} step={50} value={value} onChange={onChange} >
                <RangeSliderTrack bg='raano.border' height='3px' ><RangeSliderFilledTrack bg='raano.black' /></RangeSliderTrack>
                <RangeSliderThumb index={0} aria-label='Minimum price' boxSize='16px' border='1px solid' borderColor='raano.black' boxShadow='none' />
                <RangeSliderThumb index={1} aria-label='Maximum price' boxSize='16px' border='1px solid' borderColor='raano.black' boxShadow='none' />
            </RangeSlider>
        </Box>
    </Box>
);

// `groups` lets the phone drawer render one group at a time
export const filterGroups = ({ filters, set, categories, colors, sizes, priceBounds }) => [
    categories.length > 1 && {
        key: 'categories', title: 'Product Type', count: filters.categories.length,
        body: <ShowMoreList items={categories} render={c => <CheckRow key={c._id} checked={filters.categories.includes(c._id)} onChange={() => set('categories', toggle(filters.categories, c._id))} >{c.name}</CheckRow>} />
    },
    colors.length > 0 && {
        key: 'colors', title: 'Color', count: filters.colors.length,
        body: <ShowMoreList items={colors} render={c => <CheckRow key={c} swatch={swatchColor(c)} checked={filters.colors.includes(c)} onChange={() => set('colors', toggle(filters.colors, c))} >{c}</CheckRow>} />
    },
    sizes.length > 0 && {
        key: 'sizes', title: 'Size', count: filters.sizes.length,
        body: <ShowMoreList items={sizes} limit={6} render={s => <CheckRow key={s} checked={filters.sizes.includes(s)} onChange={() => set('sizes', toggle(filters.sizes, s))} >{s}</CheckRow>} />
    },
    priceBounds[1] > priceBounds[0] && {
        key: 'price', title: 'Price',
        body: <PriceRange value={filters.price} bounds={priceBounds} onChange={(v) => set('price', v)} />
    },
].filter(Boolean);

const ProductFilters = (props) => {
    const set = (key, value) => props.onChange({ ...props.filters, [key]: value });
    return (
        <Box>
            {filterGroups({ ...props, set }).map(g => <Group key={g.key} title={g.title} count={g.count} >{g.body}</Group>)}
        </Box>
    );
};

export default ProductFilters;
